// --- FILE: src/hooks/usePayroll.js (ENHANCED - INTEGRATED WITH NEW HOOKS) ---
import { useState, useCallback, useMemo } from 'react';
import { db } from '../services/firebase';
import { collection, query, where, getDocs, Timestamp, orderBy } from 'firebase/firestore';
import { format } from 'date-fns';
import { useShopify } from './useShopify';
// Importación actualizada: Ahora se usa useCsvExport en lugar de useExcelExport
import { useCsvExport } from './useCsvExport'; //

export const usePayroll = () => {
  // === ESTADOS PRINCIPALES ===
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);
  const [reportType, setReportType] = useState(null);
  const [reportPeriod, setReportPeriod] = useState('');
  const [reportMetadata, setReportMetadata] = useState(null);

  // === HOOKS INTEGRADOS ===
  const { 
    fetchUnifiedReport, 
    unifiedSalesData, 
    salesMetadata,
    channelBreakdown,
    getChannelStatistics,
    getPeriodStatistics,
    getExecutiveSummary,
    clearData: clearShopifyData
  } = useShopify();

  // Se desestructura el hook useCsvExport
  const { 
    exportPayrollReport: exportPayrollCsv, // Renombrado para evitar conflicto y claridad
    exportShiftsOnly: exportShiftsCsv,     // Renombrado para claridad
    exportUnifiedSales: exportSalesCsv,    // Renombrado para claridad
    exporting: exportInProgress,
    exportProgress
  } = useCsvExport(); //

  // === FUNCIÓN PRINCIPAL MEJORADA PARA REPORTES PERSONALIZADOS ===
  const calculateCustomReport = useCallback(async (employees, startDate, endDate, reportOptions = {}) => {
    setLoading(true);
    setError('');
    setReportData(null);
    setReportType('unified_custom');

    try {
      if (!startDate || !endDate) {
        throw new Error("Las fechas de inicio y fin son requeridas.");
      }
      
      const formattedPeriod = `Reporte Unificado: ${format(startDate, 'dd/MM/yy HH:mm')} - ${format(endDate, 'dd/MM/yy HH:mm')}`;
      setReportPeriod(formattedPeriod);

      console.log('🔄 Iniciando cálculo de reporte unificado:', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        employees: employees.length,
        options: reportOptions
      });

      let shiftsData = [];
      let salesData = {};
      let reportMetadata = {};

      // === 1. OBTENER DATOS DE TURNOS ===
      if (reportOptions.includeHours) {
        console.log('📊 Obteniendo datos de turnos...');

        try {
          // Consulta de Firestore para turnos
          // Estrategia: Obtenemos turnos que empiezan dentro de un rango ampliado
          // y luego filtramos en el cliente para mayor precisión

          // Ampliar el rango de búsqueda 24 horas hacia atrás para capturar turnos
          // que empezaron antes pero terminan dentro del rango seleccionado
          const searchStartDate = new Date(startDate.getTime() - 24 * 60 * 60 * 1000);

          const shiftsQuery = query(
            collection(db, 'shifts'),
            where('startTime', '>=', Timestamp.fromDate(searchStartDate)),
            where('startTime', '<=', Timestamp.fromDate(endDate)),
            orderBy('startTime', 'desc')
          );

          const shiftsSnapshot = await getDocs(shiftsQuery);
          const allShifts = shiftsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          console.log(`📥 Turnos consultados desde BD: ${allShifts.length}`);

          // Filtrar turnos que se superponen con el rango seleccionado
          // Un turno se incluye si:
          // - Comienza dentro del rango, O
          // - Termina dentro del rango, O
          // - Envuelve completamente el rango (comienza antes y termina después)
          shiftsData = allShifts.filter(shift => {
            const shiftStart = shift.startTime?.toDate();
            const shiftEnd = shift.endTime?.toDate();

            if (!shiftStart || !shiftEnd) {
              console.warn(`⚠️ Turno ${shift.id} sin startTime o endTime válido, excluyendo`);
              return false;
            }

            // Verificar si hay superposición entre [shiftStart, shiftEnd] y [startDate, endDate]
            const overlaps = shiftStart < endDate && shiftEnd > startDate;

            return overlaps;
          });

          console.log(`✅ Turnos filtrados que se superponen con el rango: ${shiftsData.length}`, {
            rangoReporte: `${startDate.toISOString()} - ${endDate.toISOString()}`,
            byEmployee: shiftsData.reduce((acc, shift) => {
              acc[shift.employeeName] = (acc[shift.employeeName] || 0) + 1;
              return acc;
            }, {})
          });

        } catch (shiftsError) {
          console.warn("⚠️ Error obteniendo turnos, continuando sin datos de horas:", shiftsError);
          setError("Advertencia: No se pudieron cargar todos los datos de turnos. Verifique sus índices de Firestore.");
        }
      }

      // === 2. OBTENER DATOS UNIFICADOS DE VENTAS ===
      if (reportOptions.includeCommissions) {
        console.log('🛒 Obteniendo datos unificados de ventas...');

        try {
          const salesResult = await fetchUnifiedReport(startDate, endDate, {
            includeOnline: true,
            includePOS: true,
            forAdmin: true,
            // Filtros de Shopify (puedes personalizar según necesidad)
            financialStatus: reportOptions.financialStatus || 'any',
            fulfillmentStatus: reportOptions.fulfillmentStatus || 'any',
            excludeTestOrders: reportOptions.excludeTestOrders || false,
            excludeDraftOrders: reportOptions.excludeDraftOrders || false
          });

          if (salesResult.success) {
            const rawSalesData = salesResult.data.sellers || {};

            console.log('📥 Datos de ventas obtenidos desde Shopify:', {
              totalSellers: Object.keys(rawSalesData).length,
              totalOrders: salesResult.data.summary?.totalOrders,
              totalSales: salesResult.data.summary?.totalSales,
              channels: Object.keys(salesResult.data.channelBreakdown || {})
            });

            // DEBUG: Ver estructura completa
            console.log('🔍 DEBUG - Estructura completa de salesResult.data:', {
              sellers: Object.keys(rawSalesData),
              summary: salesResult.data.summary,
              channelBreakdown: salesResult.data.channelBreakdown
            });

            // Procesar órdenes que ya vienen filtradas por fetchShopifyDataV2
            salesData = {};
            let filteredTotalOrders = 0;
            let filteredTotalSales = 0;
            let filteredTotalCommissions = 0;

            Object.entries(rawSalesData).forEach(([sellerName, sellerData]) => {
              // IMPORTANTE: No necesitamos filtrar por tiempo aquí porque fetchShopifyDataV2
              // ya filtró las órdenes por el rango de fechas UTC correcto
              // Simplemente incluir todas las órdenes que vienen en orderDetails

              const allOrdersForSeller = sellerData.orderDetails || [];

              console.log(`📦 ${sellerName}: ${allOrdersForSeller.length} órdenes recibidas de Shopify`);

              // DEBUG: Ver primera orden para verificar estructura
              if (allOrdersForSeller.length > 0) {
                console.log(`🔍 DEBUG - Primera orden de ${sellerName}:`, allOrdersForSeller[0]);
              }

              if (allOrdersForSeller.length > 0) {
                // Ventas netas (ya con descuentos y devoluciones)
                const totalSales = allOrdersForSeller.reduce((sum, order) => sum + (order.netSales || 0), 0);
                // Costo de lo vendido (COGS)
                const totalCOGS = allOrdersForSeller.reduce((sum, order) => sum + (order.cogs || 0), 0);
                // Ganancia comisionable = suma PRODUCTO POR PRODUCTO de max(0, venta − costo),
                // calculada en el backend (order.commissionableProfit). Un producto en oferta por
                // debajo de su costo aporta $0 y NO resta de los demás.
                // Respaldo por-orden si el dato del backend no está (datos viejos en caché).
                const totalProfit = allOrdersForSeller.reduce(
                  (sum, order) =>
                    sum +
                    (order.commissionableProfit ??
                      Math.max(0, (order.netSales || 0) - (order.cogs || 0))),
                  0
                );
                // Comisión = % sobre la GANANCIA NETA (no sobre el costo ni la venta)
                const totalCommission = sellerData.commissionRate > 0
                  ? totalProfit * (sellerData.commissionRate / 100)
                  : 0;

                salesData[sellerName] = {
                  ...sellerData,
                  sales: parseFloat(totalSales.toFixed(2)),
                  cogs: parseFloat(totalCOGS.toFixed(2)),
                  commissionBase: parseFloat(totalProfit.toFixed(2)),
                  orders: allOrdersForSeller.length,
                  commission: parseFloat(totalCommission.toFixed(2)),
                  orderDetails: allOrdersForSeller,
                  legacyOrders: 0, // No aplica con la nueva estructura
                  modernOrders: allOrdersForSeller.length
                };

                filteredTotalOrders += allOrdersForSeller.length;
                filteredTotalSales += totalSales;
                filteredTotalCommissions += totalCommission;

                console.log(`✅ ${sellerName}: ${allOrdersForSeller.length} órdenes, venta $${totalSales.toFixed(2)}, costo $${totalCOGS.toFixed(2)}, ganancia $${totalProfit.toFixed(2)}, comisión: $${totalCommission.toFixed(2)}`);
              }
            });

            console.log('✅ Datos de ventas procesados:', {
              rangoReporte: `${startDate.toISOString()} - ${endDate.toISOString()}`,
              ordenesDesdeShopify: salesResult.metadata?.ordersProcessed || salesResult.data.summary?.totalOrders,
              ordenesProcesadas: filteredTotalOrders,
              ventasTotales: filteredTotalSales.toFixed(2),
              vendedoresConVentas: Object.keys(salesData).length,
              comisionesTotales: filteredTotalCommissions.toFixed(2)
            });

            // Recalcular financialBreakdown basado en las órdenes filtradas por tiempo
            const recalculatedFinancialBreakdown = {
              grossSales: 0,
              discounts: 0,
              refunds: 0,
              netSales: 0,
              taxes: 0,
              shipping: 0,
              totalSales: 0,
              ordersCount: 0,
              cancelledOrders: 0,
              refundedOrders: 0,
              averageOrderValue: 0
            };

            // Recalcular totales financieros de todas las órdenes
            Object.values(salesData).forEach(sellerData => {
              (sellerData.orderDetails || []).forEach(order => {
                // Estructura de fetchShopifyDataV2:
                // order.grossSales = subtotal antes de descuentos
                // order.discounts = descuentos aplicados
                // order.returns = devoluciones
                // order.netSales = subtotal después de descuentos y devoluciones
                // order.taxes = impuestos
                // order.total = total final (netSales + taxes)

                const grossSales = order.grossSales || 0;
                const discounts = order.discounts || 0;
                const returns = order.returns || 0;
                const netSales = order.netSales || 0;
                const taxes = order.taxes || 0;
                const totalPrice = order.total || 0;

                recalculatedFinancialBreakdown.grossSales += grossSales;
                recalculatedFinancialBreakdown.discounts += discounts;
                recalculatedFinancialBreakdown.refunds += returns;
                recalculatedFinancialBreakdown.netSales += netSales;
                recalculatedFinancialBreakdown.taxes += taxes;
                recalculatedFinancialBreakdown.totalSales += totalPrice;
                recalculatedFinancialBreakdown.ordersCount += 1;

                if (returns > 0) {
                  recalculatedFinancialBreakdown.refundedOrders += 1;
                }
              });
            });

            // Redondear y calcular promedio
            Object.keys(recalculatedFinancialBreakdown).forEach(key => {
              if (key !== 'ordersCount' && key !== 'cancelledOrders' && key !== 'refundedOrders' && key !== 'averageOrderValue') {
                recalculatedFinancialBreakdown[key] = parseFloat(recalculatedFinancialBreakdown[key].toFixed(2));
              }
            });

            if (recalculatedFinancialBreakdown.ordersCount > 0) {
              recalculatedFinancialBreakdown.averageOrderValue = parseFloat(
                (recalculatedFinancialBreakdown.totalSales / recalculatedFinancialBreakdown.ordersCount).toFixed(2)
              );
            }

            reportMetadata = {
              ...salesResult.metadata,
              channelBreakdown: salesResult.data.channelBreakdown,
              periodInfo: salesResult.data.periodInfo,
              // REEMPLAZAR el financialBreakdown original con el recalculado
              financialBreakdown: recalculatedFinancialBreakdown,
              // Agregar info de filtrado
              filteredStats: {
                totalOrders: filteredTotalOrders,
                totalSales: parseFloat(filteredTotalSales.toFixed(2)),
                totalCommissions: parseFloat(filteredTotalCommissions.toFixed(2)),
                sellersCount: Object.keys(salesData).length
              }
            };

          } else {
            throw new Error(salesResult.error || 'Error en datos de ventas');
          }

        } catch (salesError) {
          console.warn("⚠️ Error obteniendo ventas, continuando sin datos de comisiones:", salesError);
          if (!reportOptions.includeHours) {
            // Si solo se solicitaron comisiones y falló, es un error crítico
            throw new Error("No se pudieron obtener los datos de ventas solicitados.");
          }
        }
      }

      // === 3. COMBINAR Y PROCESAR DATOS ===
      console.log('🔧 Combinando datos de turnos y ventas...');
      
      const combinedReport = employees.map(emp => {
        // Procesar turnos del empleado
        const empShifts = shiftsData.filter(shift => shift.employeeId === emp.id);
        const totalMinutes = empShifts.reduce((acc, shift) => acc + (shift.durationMinutes || 0), 0);
        // NOTA: Se usa emp.hourlyRate para el cálculo de ganancias, lo cual es correcto.
        // Asegúrate de que este valor esté presente y sea preciso en tu colección 'sellers'.
        const totalEarnings = (totalMinutes / 60) * (emp.hourlyRate || 10.50);

        // Procesar ventas del empleado (buscar por nombre normalizado)
        // Intentar primero con el nombre exacto, luego variaciones comunes
        let empSalesData = salesData[emp.name];

        // Si no se encuentra, verificar si hay ventas bajo "Sin asignar" o variaciones del nombre
        if (!empSalesData && Object.keys(salesData).length > 0) {
          console.log(`⚠️ No se encontraron ventas para "${emp.name}". Vendedores disponibles:`, Object.keys(salesData));
        }

        empSalesData = empSalesData || {
          sales: 0,
          cogs: 0,
          commissionBase: 0,
          orders: 0,
          commission: 0,
          orderDetails: [],
          legacyOrders: 0,
          modernOrders: 0
        };
        
        // Información para acciones de turnos
        const shiftsCount = empShifts.length;
        // Permitir editar turnos si hay al menos uno
        // Si hay un solo turno, mostramos el botón de edición rápida en la tabla
        // Si hay múltiples turnos, el usuario puede editarlos desde el DetailedShiftsModal
        const canEdit = shiftsCount === 1;
        const editableShift = canEdit ? empShifts[0] : null;
        
        // Calcular totales
        const totalToPay = parseFloat((totalEarnings + empSalesData.commission).toFixed(2));

        const employeeReport = {
          // Información básica
          employeeId: emp.id,
          employeeName: emp.name,
          hourlyRate: emp.hourlyRate || 10.50, // Usar la tarifa del empleado o un valor por defecto
          
          // Datos de turnos
          totalHours: totalMinutes / 60,
          totalEarnings: parseFloat(totalEarnings.toFixed(2)),
          shiftsCount,
          shifts: empShifts, // Incluir shifts detallados para la exportación y el modal de detalles
          
          // Datos de ventas
          sales: empSalesData.sales,
          cogs: empSalesData.cogs || 0,
          commissionBase: empSalesData.commissionBase ?? 0, // Base de comisión = ganancia neta (ventas − costo)
          orders: empSalesData.orders,
          commission: empSalesData.commission,
          orderDetails: empSalesData.orderDetails || [], // Incluir detalles de órdenes para exportación
          legacyOrders: empSalesData.legacyOrders || 0,
          modernOrders: empSalesData.modernOrders || 0,
          
          // Totales
          totalToPay,
          
          // Información para acciones en la UI
          canEdit,
          editableShift,
          
          // Estadísticas adicionales
          averageShiftHours: shiftsCount > 0 ? (totalMinutes / 60) / shiftsCount : 0,
          averageOrderValue: empSalesData.orders > 0 ? empSalesData.sales / empSalesData.orders : 0,
          commissionRate: empSalesData.commission > 0 ? 2 : 0, // Tasa de comisión fija del 2% si aplica
          
          // Flags de estado
          hasHours: totalMinutes > 0,
          hasCommissions: empSalesData.commission > 0,
          hasSales: empSalesData.sales > 0,
          hasActivity: totalMinutes > 0 || empSalesData.commission > 0 || empSalesData.sales > 0
        };

        console.log(`📝 Procesado empleado ${emp.name}:`, {
          shifts: shiftsCount,
          hours: employeeReport.totalHours,
          sales: empSalesData.sales,
          commission: empSalesData.commission,
          total: totalToPay
        });

        return employeeReport;
      })
      .filter(emp => emp.hasActivity); // Solo empleados con actividad en el período

      // AGREGAR VENTAS "SIN ASIGNAR" COMO ENTRADA ADICIONAL SI EXISTEN
      if (salesData['Sin asignar']) {
        const unassignedSales = salesData['Sin asignar'];
        combinedReport.push({
          employeeId: 'sin-asignar',
          employeeName: '⚠️ Ventas Sin Asignar',
          hourlyRate: 0,
          totalHours: 0,
          totalEarnings: 0,
          shiftsCount: 0,
          shifts: [],
          sales: unassignedSales.sales,
          cogs: unassignedSales.cogs || 0,
          commissionBase: unassignedSales.commissionBase ?? 0,
          orders: unassignedSales.orders,
          commission: unassignedSales.commission,
          orderDetails: unassignedSales.orderDetails || [],
          legacyOrders: 0,
          modernOrders: unassignedSales.orders,
          totalToPay: unassignedSales.commission,
          canEdit: false,
          editableShift: null,
          averageShiftHours: 0,
          averageOrderValue: unassignedSales.orders > 0 ? unassignedSales.sales / unassignedSales.orders : 0,
          commissionRate: unassignedSales.commissionRate || 0,
          hasHours: false,
          hasCommissions: unassignedSales.commission > 0,
          hasSales: unassignedSales.sales > 0,
          hasActivity: true
        });

        console.log(`⚠️ Se agregaron ${unassignedSales.orders} ventas sin asignar: $${unassignedSales.sales.toFixed(2)}`);
      }

      combinedReport.sort((a, b) => b.totalToPay - a.totalToPay); // Ordenar por total a pagar (descendente)

      console.log('✅ Reporte combinado generado:', {
        totalEmployees: combinedReport.length,
        totalHours: combinedReport.reduce((acc, emp) => acc + emp.totalHours, 0),
        totalEarnings: combinedReport.reduce((acc, emp) => acc + emp.totalEarnings, 0),
        totalCommissions: combinedReport.reduce((acc, emp) => acc + emp.commission, 0),
        totalToPay: combinedReport.reduce((acc, emp) => acc + emp.totalToPay, 0)
      });

      // Guardar datos en el estado del hook
      setReportData(combinedReport);
      setReportMetadata({
        ...reportMetadata, // Incluye metadatos de Shopify si se obtuvieron
        generatedAt: new Date().toISOString(),
        employeesCount: combinedReport.length,
        shiftsCount: shiftsData.length,
        reportOptions,
        summary: {
          totalHours: combinedReport.reduce((acc, emp) => acc + emp.totalHours, 0),
          totalEarnings: combinedReport.reduce((acc, emp) => acc + emp.totalEarnings, 0),
          totalCommissions: combinedReport.reduce((acc, emp) => acc + emp.commission, 0),
          totalToPay: combinedReport.reduce((acc, emp) => acc + emp.totalToPay, 0)
        }
      });

    } catch (err) {
      console.error("❌ Error calculando reporte personalizado:", err);
      setError(`No se pudo generar el reporte: ${err.message}`);
      setReportData(null);
      setReportMetadata(null);
    } finally {
      setLoading(false);
    }
  }, [fetchUnifiedReport]);

  // === FUNCIÓN PARA EXPORTAR REPORTE ACTUAL (ahora CSV) ===
  const exportCurrentReport = useCallback(async (options = {}) => {
    if (!reportData || reportData.length === 0) {
      throw new Error('No hay datos de reporte para exportar');
    }

    const {
      filename = 'reporte_nomina',
      includeShiftsDetail = true,
      includeSalesDetail = true,
      includeExecutiveSummary = true
    } = options;

    try {
      // Se llama a la función de exportación de CSV para el reporte de nómina
      const result = await exportPayrollCsv(reportData, { //
        filename,
        includeShiftsDetail,
        includeSalesDetail,
        includeExecutiveSummary,
        dateRange: reportPeriod,
        reportType: reportType
      });

      if (result.success) {
        console.log('✅ Reporte exportado exitosamente:', result.filename);
        return {
          success: true,
          // El mensaje ahora indica que son múltiples CSVs si aplica
          message: `Reporte exportado como ${result.filename} (múltiples archivos CSV)` 
        };
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Error exportando reporte:', error);
      throw error;
    }
  }, [reportData, reportPeriod, reportType, exportPayrollCsv]); //

  // === FUNCIÓN PARA EXPORTAR TURNOS DE UN EMPLEADO (ahora CSV) ===
  const exportEmployeeShifts = useCallback(async (employeeId, options = {}) => {
    if (!reportData) {
      throw new Error('No hay datos de reporte cargados');
    }

    const employee = reportData.find(emp => emp.employeeId === employeeId);
    if (!employee || !employee.shifts || employee.shifts.length === 0) {
      throw new Error('No se encontraron turnos para este empleado');
    }

    const { filename } = options;
    const employeeName = employee.employeeName.replace(/\s+/g, '_');
    
    try {
      // Se llama a la función de exportación de CSV para turnos
      const result = await exportShiftsCsv( //
        employee.shifts, 
        employee.employeeName,
        {
          filename: filename || `turnos_${employeeName}`,
          dateRange: reportPeriod
        }
      );

      if (result.success) {
        return {
          success: true,
          filename: result.filename,
          message: `Turnos de ${employee.employeeName} exportados como ${result.filename}`
        };
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Error exportando turnos del empleado:', error);
      throw error;
    }
  }, [reportData, reportPeriod, exportShiftsCsv]); //

  // === FUNCIÓN PARA EXPORTAR VENTAS UNIFICADAS (ahora CSV) ===
  const exportSalesReport = useCallback(async (options = {}) => {
    if (!unifiedSalesData) {
      throw new Error('No hay datos de ventas unificadas disponibles');
    }

    try {
      // Se llama a la función de exportación de CSV para ventas
      const result = await exportSalesCsv(unifiedSalesData, { //
        filename: options.filename || 'ventas_unificadas',
        includeOrderDetails: options.includeOrderDetails !== false,
        includeChannelBreakdown: options.includeChannelBreakdown !== false
      });

      if (result.success) {
        return {
          success: true,
          // El mensaje ahora indica que son múltiples CSVs si aplica
          message: `Ventas unificadas exportadas como ${result.filename} (múltiples archivos CSV)`
        };
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Error exportando ventas unificadas:', error);
      throw error;
    }
  }, [unifiedSalesData, exportSalesCsv]); //

  // === FUNCIÓN PARA OBTENER ESTADÍSTICAS DEL REPORTE ===
  const getReportStatistics = useCallback(() => {
    if (!reportData || !reportMetadata) return null;

    const stats = {
      summary: reportMetadata.summary,
      breakdown: {
        employeesWithHours: reportData.filter(emp => emp.hasHours).length,
        employeesWithCommissions: reportData.filter(emp => emp.hasCommissions).length,
        employeesWithBoth: reportData.filter(emp => emp.hasHours && emp.hasCommissions).length,
        totalShifts: reportData.reduce((acc, emp) => acc + emp.shiftsCount, 0),
        averageHoursPerEmployee: reportData.length > 0 ? 
          reportMetadata.summary.totalHours / reportData.length : 0
      },
      channels: getChannelStatistics(),
      periods: getPeriodStatistics(),
      executive: getExecutiveSummary()
    };

    return stats;
  }, [reportData, reportMetadata, getChannelStatistics, getPeriodStatistics, getExecutiveSummary]);

  // === FUNCIÓN PARA BUSCAR EMPLEADO EN EL REPORTE ===
  const findEmployeeInReport = useCallback((employeeId) => {
    if (!reportData) return null;
    return reportData.find(emp => emp.employeeId === employeeId) || null;
  }, [reportData]);

  // === FUNCIÓN PARA LIMPIAR DATOS ===
  const clearReportData = useCallback(() => {
    setReportData(null);
    setReportMetadata(null);
    setReportType(null);
    setReportPeriod('');
    setError('');
    clearShopifyData();
  }, [clearShopifyData]);

  // === COMPUTED VALUES ===
  const isReportReady = useMemo(() => {
    return reportData && reportData.length > 0;
  }, [reportData]);

  const canExport = useMemo(() => {
    return isReportReady && !loading && !exportInProgress;
  }, [isReportReady, loading, exportInProgress]);

  const reportSummary = useMemo(() => {
    if (!reportData || reportData.length === 0) return null;

    return {
      totalEmployees: reportData.length,
      totalHours: reportData.reduce((acc, emp) => acc + emp.totalHours, 0),
      totalEarnings: reportData.reduce((acc, emp) => acc + emp.totalEarnings, 0),
      totalCommissions: reportData.reduce((acc, emp) => acc + emp.commission, 0),
      totalToPay: reportData.reduce((acc, emp) => acc + emp.totalToPay, 0),
      hasChannelData: !!channelBreakdown,
      hasMetadata: !!reportMetadata
    };
  }, [reportData, channelBreakdown, reportMetadata]);

  return {
    // === ESTADOS PRINCIPALES ===
    loading,
    error,
    reportData,
    reportType,
    reportPeriod,
    reportMetadata,

    // === ESTADOS DERIVADOS ===
    isReportReady,
    canExport,
    reportSummary,
    exportInProgress,
    exportProgress,

    // === DATOS INTEGRADOS DE SHOPIFY ===
    unifiedSalesData,
    salesMetadata,
    channelBreakdown,

    // === FUNCIONES PRINCIPALES ===
    calculateCustomReport,

    // === FUNCIONES DE EXPORTACIÓN (AHORA CSV) ===
    exportCurrentReport,
    exportEmployeeShifts,
    exportSalesReport,

    // === FUNCIONES DE ANÁLISIS ===
    getReportStatistics,
    getChannelStatistics,
    getPeriodStatistics,
    getExecutiveSummary,
    findEmployeeInReport,

    // === UTILIDADES ===
    clearReportData,
    clearShopifyData // Exportar función de limpiar datos de Shopify
  };
};